import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from "react";
import { CircleHelp, Globe, Loader2, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/modern/components/ui/label";
import { Switch } from "@/modern/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Button } from "@/modern/components/ui/button";
import { FileUpload, FileUploadDropzone, FileUploadTrigger } from "@/modern/components/ui/file-upload";
import { marketingPagesApi, sitesApi } from "@/network/network.jsx";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { Site } from "@/bryntum-lib/models/Site";
import { mergeOgImageApiIntoMarketingPage } from "@/react/schema/marketingPageSchema";
import { mergeOgImageApiIntoSite } from "@/react/schema/siteSchema";
import { VIEW_EDITOR_CONTROL_CLASS } from "@/modern/components/editors/viewEditor/components/ViewEditorSection";
import { cn } from "@/modern/utils";
import { MARKETING_PUBLIC_HOST } from "../../utils";

export const INPUT_CLASS = cn(
  VIEW_EDITOR_CONTROL_CLASS,
  "!border-input !bg-background !shadow-none focus-visible:!ring-2 focus-visible:!ring-primary/25",
);

/** Uniform height/padding for go-live modal inputs. */
export const GO_LIVE_CONTROL_CLASS =
  "h-9 px-3 text-sm shadow-sm focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

/** Go-live modal controls — transparent fields on bg-background (PublishModalV4 parity). */
export const GO_LIVE_INPUT_CLASS = cn(GO_LIVE_CONTROL_CLASS, "md:text-sm");

export const GO_LIVE_TEXTAREA_CLASS = cn(
  "min-h-[80px] resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0",
);

/** Inputs sitting on bg-card surfaces — slightly lifted neutral fill. */
export const PANEL_INPUT_CLASS = cn(
  INPUT_CLASS,
  "!bg-muted/25 dark:!bg-background/80",
);

export const TEXTAREA_CLASS = cn(
  "min-h-[72px] resize-y rounded-md border border-input bg-background px-3 py-2 text-sm",
  "shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25",
);

export const PANEL_TEXTAREA_CLASS = cn(
  TEXTAREA_CLASS,
  "bg-muted/25 dark:bg-background/80",
);

/** Raised block on the inspector canvas — matches Coverage hub rows & Promote cards. */
export function SurfaceBlock({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border bg-card shadow-sm",
        padded && "p-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PanelSection({
  label,
  hint,
  children,
  className,
  surface = true,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
  /** Wrap fields in a raised SurfaceBlock (inspector default). */
  surface?: boolean;
}) {
  return (
    <section className={cn("space-y-2", className)}>
      <div>
        <h4 className="text-sm font-medium text-muted-foreground">{label}</h4>
        {hint && (
          <p className="mt-0.5 text-xs leading-snug text-muted-foreground/80">{hint}</p>
        )}
      </div>
      {surface ? (
        <SurfaceBlock>
          <div className="space-y-4">{children}</div>
        </SurfaceBlock>
      ) : (
        <div className="space-y-4">{children}</div>
      )}
    </section>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
  variant = "panel",
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
  /** `goLive` — flat fields on bg-background; `panel` — inspector sidebar. */
  variant?: "panel" | "goLive";
}) {
  return (
    <div className={variant === "goLive" ? "space-y-2" : "space-y-1.5"}>
      <Label
        className={
          variant === "goLive"
            ? "text-sm font-medium"
            : "text-xs font-medium text-foreground/80"
        }
      >
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && !error && (
        <div className="text-xs leading-snug text-muted-foreground">{hint}</div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function SettingsToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor={id} className="cursor-pointer text-sm font-normal text-foreground">
          {label}
        </Label>
        {description && (
          <p className="text-xs leading-snug text-muted-foreground">{description}</p>
        )}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

export function FormSectionHelpTrigger({
  sectionLabel,
  helpText,
  className,
}: {
  sectionLabel: string;
  helpText: string;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors",
            "hover:bg-muted hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            className,
          )}
          aria-label={`What is ${sectionLabel}?`}
        >
          <CircleHelp className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={6}
        className="max-w-[280px] border text-left"
      >
        {helpText}
      </TooltipContent>
    </Tooltip>
  );
}

export function GoLiveFormSection({
  title,
  subtitle,
  helpText,
  children,
  first = false,
  className,
}: {
  title: string;
  subtitle?: string;
  helpText?: string;
  children: ReactNode;
  first?: boolean;
  className?: string;
}) {
  return (
    <section className={cn(!first && "border-t border-border pt-6", className)}>
      <div className="mb-4">
        <div className="flex min-h-7 items-center justify-between gap-2">
          <h3 className="text-sm font-medium leading-none text-foreground">{title}</h3>
          {helpText != null ? (
            <FormSectionHelpTrigger sectionLabel={title} helpText={helpText} />
          ) : null}
        </div>
        {subtitle != null ? (
          <p className="mt-0.5 text-sm text-muted-foreground/70">{subtitle}</p>
        ) : null}
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function includedPagesLabel(includedCount: number) {
  if (includedCount === 0) return "No pages included";
  if (includedCount === 1) return "1 page included";
  return `${includedCount} pages included`;
}

function SiteStatusBadge({ isLive }: { isLive: boolean }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-medium",
        isLive ? "bg-success/15 text-success" : "bg-chart-4/15 text-chart-4",
      )}
    >
      {isLive ? "Live" : "Draft"}
    </span>
  );
}

export function SiteEditorHeader({
  title,
  icon: Icon,
  isLive,
  isReadOnly,
  trailing,
  className,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  isLive: boolean;
  isReadOnly?: boolean;
  trailing: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-6 py-3",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary ring-1 ring-inset ring-primary/15">
          <Icon className="size-4" aria-hidden />
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
          <SiteStatusBadge isLive={isLive} />
          {isReadOnly && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              View only
            </span>
          )}
        </div>
      </div>
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">{trailing}</div>
    </header>
  );
}

export function SiteEditorFooter({
  subtitle,
  includedCount,
  statusChipId,
  className,
}: {
  subtitle: string;
  includedCount: number;
  statusChipId?: string;
  className?: string;
}) {
  const pagesLabel = includedPagesLabel(includedCount);
  const pagesWarning = includedCount === 0;

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-3",
        className,
      )}
    >
      <p className="text-sm text-muted-foreground">{subtitle}</p>
      <span
        id={statusChipId}
        role="status"
        aria-live="polite"
        className={cn(
          "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium",
          pagesWarning ? "bg-chart-4/15 text-chart-4" : "bg-muted text-muted-foreground",
        )}
      >
        {pagesLabel}
      </span>
    </div>
  );
}

export function SiteEditorPreviewHeader({ className }: { className?: string }) {
  return (
    <header
      className={cn(
        "flex shrink-0 items-center border-b border-border bg-muted/30 px-6 py-3",
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-base font-semibold tracking-tight text-foreground">Live preview</p>
        <p className="mt-1 text-sm text-muted-foreground">Search and social share appearance</p>
      </div>
    </header>
  );
}

export function GoLiveToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card/50 px-4 py-3">
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor={id} className="cursor-pointer text-sm font-medium text-foreground">
          {label}
        </Label>
        {description != null ? (
          <p className="text-xs leading-snug text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

export function InlineToggleField({
  id,
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <GoLiveToggleRow
      id={id}
      label={label}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
    />
  );
}

export function AutoFillLink({
  onClick,
  disabled,
  loading,
  tooltip,
  className,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
  className?: string;
}) {
  const button = (
    <button
      type="button"
      disabled={disabled || loading}
      aria-busy={loading}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary/80 disabled:opacity-50",
        disabled || loading ? "cursor-not-allowed" : "hover:text-primary/80",
        className,
      )}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
      Auto-fill SEO from your board
    </button>
  );

  if (tooltip && disabled && !loading) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[260px] border text-left">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}

function previewHostFromUrl(url: string) {
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname;
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0] || url;
  }
}

export function GooglePreview({
  title,
  url,
  description,
  hero = false,
  fit = false,
}: {
  title: string;
  url: string;
  description: string;
  hero?: boolean;
  fit?: boolean;
}) {
  const host = previewHostFromUrl(url);

  if (hero) {
    return (
      <div
        className={cn(
          "rounded-xl border border-border bg-background shadow-sm",
          fit ? "shrink-0 p-4" : "p-5",
        )}
      >
        <div className={cn("flex items-center gap-2", fit ? "mb-2" : "mb-3")}>
          <div
            className={cn(
              "flex items-center justify-center rounded-full bg-muted",
              fit ? "size-7" : "size-8",
            )}
          >
            <Globe className={cn("text-muted-foreground", fit ? "size-3.5" : "size-4")} />
          </div>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-xs text-foreground">{host}</p>
            <p className="truncate text-xs text-muted-foreground">{url}</p>
          </div>
        </div>
        <p
          className={cn(
            "text-primary transition-opacity duration-150",
            fit ? "line-clamp-2 text-base leading-snug" : "text-xl",
          )}
        >
          {title || "Your site title"}
        </p>
        <p
          className={cn(
            "mt-1 text-muted-foreground transition-opacity duration-150",
            fit ? "line-clamp-2 text-xs leading-relaxed" : "text-sm",
          )}
        >
          {description || "Meta description preview."}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="truncate text-sm text-primary">{url || "yoursite.com/your-slug"}</p>
      <p className="mt-1 truncate text-base font-medium text-foreground">{title || "Your site title"}</p>
      <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
        {description || "Meta description preview."}
      </p>
    </div>
  );
}

function normalizePreviewImageUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidPreviewImageUrl(url: string): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

const OG_IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/gif";
const OG_IMAGE_EXTENSIONS = ["png", "jpeg", "jpg", "webp", "gif"];

function validateOgImageFile(file: File): boolean {
  const ext = file.type.split("/")[1]?.toLowerCase();
  if (!file.type.includes("image") || !ext || !OG_IMAGE_EXTENSIONS.includes(ext)) {
    toast.error("Upload a PNG, JPEG, WebP, or GIF image.");
    return false;
  }
  return true;
}

export function ShareImageUploader({
  previewSrc,
  disabled,
  onUpload,
  onRemove,
}: {
  previewSrc: string;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file || disabled) return;
      if (!validateOgImageFile(file)) return;

      setIsUploading(true);
      try {
        await onUpload(file);
      } finally {
        setIsUploading(false);
      }
    },
    [disabled, onUpload],
  );

  const handleRemove = useCallback(async () => {
    if (disabled || !previewSrc) return;

    setIsUploading(true);
    try {
      await onRemove();
    } finally {
      setIsUploading(false);
    }
  }, [disabled, previewSrc, onRemove]);

  if (previewSrc) {
    return (
      <div className="space-y-3">
        <div className="relative aspect-[1.91/1] w-full overflow-hidden rounded-lg border border-border bg-muted/30">
          <img
            src={normalizePreviewImageUrl(previewSrc)}
            alt=""
            className="size-full object-cover"
          />
        </div>
        <div className="flex gap-2">
          <FileUpload
            accept={OG_IMAGE_ACCEPT}
            maxFiles={1}
            onAccept={(files) => void handleUpload(files)}
            disabled={disabled || isUploading}
            className="flex-1"
          >
            <FileUploadTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={disabled || isUploading}
              >
                {isUploading ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <Upload className="size-4" aria-hidden />
                )}
                Replace
              </Button>
            </FileUploadTrigger>
          </FileUpload>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={disabled || isUploading}
            onClick={() => void handleRemove()}
          >
            <X className="size-4" aria-hidden />
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FileUpload
      accept={OG_IMAGE_ACCEPT}
      maxFiles={1}
      onAccept={(files) => void handleUpload(files)}
      disabled={disabled || isUploading}
    >
      <FileUploadDropzone className="min-h-28 rounded-lg border-dashed">
        <div className="text-center">
          {isUploading ? (
            <Loader2 className="mx-auto mb-2 size-8 animate-spin text-muted-foreground" aria-hidden />
          ) : (
            <Upload className="mx-auto mb-2 size-8 text-muted-foreground" aria-hidden />
          )}
          <p className="text-sm text-muted-foreground">
            {isUploading ? "Uploading…" : "Click or drag to upload share image"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">PNG, JPEG, WebP, or GIF · 1200×630 recommended</p>
        </div>
      </FileUploadDropzone>
    </FileUpload>
  );
}

export function OgImageUploader({
  site,
  boardSlug,
  disabled,
  onUpdated,
}: {
  site: Site;
  boardSlug: string | undefined;
  disabled?: boolean;
  onUpdated: () => void;
}) {
  const previewSrc = site.ogImageThumbnailUrl?.trim() || site.ogImageUrl?.trim() || "";

  const onUpload = useCallback(
    async (file: File) => {
      if (!boardSlug) return;
      try {
        const res = await sitesApi(boardSlug).uploadOgImage(file);
        mergeOgImageApiIntoSite(site, res?.data ?? {});
        onUpdated();
        toast.success("Share image uploaded.");
      } catch {
        toast.error("Could not upload share image.");
      }
    },
    [boardSlug, site, onUpdated],
  );

  const onRemove = useCallback(async () => {
    if (!boardSlug) return;
    try {
      const res = await sitesApi(boardSlug).removeOgImage();
      mergeOgImageApiIntoSite(site, res?.data ?? { og_image_url: "", og_image_thumbnail_url: "" });
      onUpdated();
      toast.success("Share image removed.");
    } catch {
      toast.error("Could not remove share image.");
    }
  }, [boardSlug, site, onUpdated]);

  return (
    <ShareImageUploader
      previewSrc={previewSrc}
      disabled={disabled}
      onUpload={onUpload}
      onRemove={onRemove}
    />
  );
}

export function MarketingPageOgImageUploader({
  page,
  boardSlug,
  disabled,
  onUpdated,
}: {
  page: MarketingPage;
  boardSlug: string | undefined;
  disabled?: boolean;
  onUpdated: () => void;
}) {
  const previewSrc = page.ogImageThumbnailUrl?.trim() || page.ogImageUrl?.trim() || "";

  const onUpload = useCallback(
    async (file: File) => {
      if (!boardSlug || page.id == null) return;
      try {
        const res = await marketingPagesApi(boardSlug).uploadOgImage(page.id, file);
        mergeOgImageApiIntoMarketingPage(page, res?.data ?? {});
        onUpdated();
        toast.success("Share image uploaded.");
      } catch {
        toast.error("Could not upload share image.");
      }
    },
    [boardSlug, page, onUpdated],
  );

  const onRemove = useCallback(async () => {
    if (!boardSlug || page.id == null) return;
    try {
      const res = await marketingPagesApi(boardSlug).removeOgImage(page.id);
      mergeOgImageApiIntoMarketingPage(page, res?.data ?? { og_image_url: "", og_image_thumbnail_url: "" });
      onUpdated();
      toast.success("Share image removed.");
    } catch {
      toast.error("Could not remove share image.");
    }
  }, [boardSlug, page, onUpdated]);

  return (
    <ShareImageUploader
      previewSrc={previewSrc}
      disabled={disabled}
      onUpload={onUpload}
      onRemove={onRemove}
    />
  );
}

function SocialImage({ imageUrl, fit = false }: { imageUrl: string; fit?: boolean }) {
  const normalizedUrl = normalizePreviewImageUrl(imageUrl);
  const canLoad = isValidPreviewImageUrl(normalizedUrl);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [normalizedUrl]);

  if (!canLoad || failed) {
    return (
      <div className="flex size-full items-center justify-center bg-muted">
        <Globe className={cn("text-muted-foreground", fit ? "size-8" : "size-10")} aria-hidden />
      </div>
    );
  }

  return (
    <img
      key={normalizedUrl}
      src={normalizedUrl}
      alt=""
      className="size-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

export function SocialPreview({
  title,
  description,
  imageUrl,
  hero = false,
  fit = false,
}: {
  title: string;
  description: string;
  imageUrl: string;
  hero?: boolean;
  fit?: boolean;
}) {
  if (hero) {
    return (
      <div
        className={cn(
          "overflow-hidden rounded-xl border border-border bg-background shadow-sm",
          fit ? "flex min-h-0 flex-1 flex-col" : undefined,
        )}
      >
        <div
          className={cn(
            "w-full overflow-hidden",
            fit ? "min-h-0 flex-1" : "aspect-[1.91/1]",
          )}
        >
          <SocialImage imageUrl={imageUrl} fit={fit} />
        </div>
        <div className={cn(fit ? "shrink-0 p-3" : "p-4")}>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {MARKETING_PUBLIC_HOST}
          </p>
          <p
            className={cn(
              "font-medium text-foreground transition-opacity duration-150",
              fit ? "mt-0.5 line-clamp-1 text-sm" : "mt-1",
            )}
          >
            {title || "Share title"}
          </p>
          <p
            className={cn(
              "text-muted-foreground transition-opacity duration-150",
              fit ? "mt-0.5 line-clamp-1 text-xs" : "mt-0.5 line-clamp-2 text-sm",
            )}
          >
            {description || "Share description preview."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <div className="aspect-[1.91/1] w-full overflow-hidden">
        <SocialImage imageUrl={imageUrl} />
      </div>
      <div className="p-3">
        <p className="truncate text-sm font-medium text-foreground">{title || "Share title"}</p>
        <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
          {description || "Share description preview."}
        </p>
      </div>
    </div>
  );
}

export function LivePreviewPanel({
  title,
  url,
  description,
  socialTitle,
  socialDescription,
  imageUrl,
  fit = false,
}: {
  title: string;
  url: string;
  description: string;
  socialTitle: string;
  socialDescription: string;
  imageUrl: string;
  fit?: boolean;
}) {
  return (
    <div className={cn(fit ? "flex h-full min-h-0 flex-col gap-3" : "space-y-6")}>
      <GooglePreview title={title} url={url} description={description} hero fit={fit} />
      <SocialPreview
        title={socialTitle}
        description={socialDescription}
        imageUrl={imageUrl}
        hero
        fit={fit}
      />
    </div>
  );
}
